import React, { useReducer, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { State } from 'state';
import fetchTeamMembers from '../../../../settings/state/actions/fetch-team-users';
import { addUserSuccess } from '../../../../moderation-v2/users/state/actions';
import User from '../../../../../models/user';
import { createLiveBlogEvent, editLiveBlogEvent, fetchLiveBlogEvent } from '../../state/actions';
import { LiveBlogModalMode, LiveEvent, LiveBlogModalView } from '../../state/types';
import { isTeamMembersFetched } from '../../../../moderation-v2/users/state/reducer';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';
import {
  LiveBlogModalReducer,
  initState,
  liveBlogModalReducer,
  ILiveBlogModalState,
  TAction,
  isFormValid
} from './reducer';
import EventDetails from './views/EventDetails';
import Reporters from './views/Reporters';
import LiveEventEmbedCode from './views/EmbedCode';

interface Props {
  mode: LiveBlogModalMode;
  event: LiveEvent;
  view?: LiveBlogModalView;
  closeModal: () => void;
}

const LiveBlogModal: React.FC<Props> = ({
  mode = LiveBlogModalMode.Create,
  view = LiveBlogModalView.EventDetails,
  event = {},
  closeModal
}) => {
  const globalDispatch = useDispatch();
  const [state, dispatch] = useReducer<LiveBlogModalReducer, ILiveBlogModalState>(
    liveBlogModalReducer,
    initState({ event, mode, view }),
    state => state
  );
  const isTeamFetched = useSelector<State, boolean>(isTeamMembersFetched);
  const isEditMode = mode === LiveBlogModalMode.Edit;
  const title = event.title ? state.displayName : 'Create an Event';

  const isValidForm = useMemo(() => {
    return isFormValid(state, { mode, view: state.view });
  }, [state]);

  const onViewChange = (view: LiveBlogModalView) => {
    if (state.view !== view) {
      dispatch({ type: TAction.ViewChange, payload: { view } });
    }
  };

  const createEvent = () => {
    dispatch({ type: TAction.CreateOrUpdateEvent });
    globalDispatch(
      createLiveBlogEvent({ ...state, notifyEventCreated: notifyEventCreatedOrUpdated })
    );
  };

  const editEvent = () => {
    dispatch({ type: TAction.CreateOrUpdateEvent });
    globalDispatch(
      editLiveBlogEvent({ ...state, notifyEventUpdated: notifyEventCreatedOrUpdated })
    );
  };

  const notifyEventCreatedOrUpdated = ({
    liveEvent = {},
    status = 'success'
  }: {
    liveEvent?: Partial<LiveEvent>;
    status: 'success' | 'failure';
  }) => {
    dispatch({ type: TAction.CreateOrUpdateEventSuccess, payload: { liveEvent, status } });
  };

  const addReporter = (user: User) => {
    globalDispatch(addUserSuccess({ user }));
    dispatch({ type: TAction.AddReporter, payload: { userId: user.get('id') } });
  };

  const removeReporter = (user: User) => {
    dispatch({ type: TAction.RemoveReporter, payload: { userId: user.get('id') } });
  };

  // Fetch event details
  useEffect(() => {
    if (event.id && !event.isFetched) {
      globalDispatch(fetchLiveBlogEvent({ eventId: event.id }));
    }
  }, [event]);

  // Fetch team members for reporter suggestions
  useEffect(() => {
    if (!isTeamFetched) {
      globalDispatch(fetchTeamMembers());
    }
  }, []);

  return (
    <div className={'live-blog-modal'}>
      <ModalHeader
        title={title}
        showNavigation={isEditMode}
        view={state.view}
        onViewChange={onViewChange}
      />
      <div className={'live-blog-modal-body'}>
        {state.view === LiveBlogModalView.EventDetails && (
          <EventDetails
            dispatch={dispatch}
            displayName={state.displayName}
            timezone={state.timezone}
            startTime={state.startTime}
            endTime={state.endTime}
            timeError={state.timeError}
          />
        )}
        {state.view === LiveBlogModalView.Team && (
          <Reporters
            eventId={event.id}
            reporterIds={state.reporterIds}
            addReporter={addReporter}
            removeReporter={removeReporter}
            error={state.reportersError}
          />
        )}
        {state.view === LiveBlogModalView.EmbedCode && (
          <LiveEventEmbedCode eventCode={state.eventCode} />
        )}
      </div>
      <ModalFooter
        mode={mode}
        view={state.view}
        closeModal={closeModal}
        onViewChange={onViewChange}
        isValidForm={isValidForm}
        createEvent={createEvent}
        editEvent={editEvent}
        isLoading={state.isLoading}
      />
    </div>
  );
};

export default LiveBlogModal;
